import { cloneElement, useRef, type HTMLAttributes, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import { Link } from 'react-router';
import { useOnClickOutside } from '~/components/ui/hooks';
import Icon, { type IconNames } from '~/components/ui/Icon';
import { useModal } from '~/components/ui/Modal';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/avatar.scss';
import type { Company } from '~/types/platform';
import type { CountryCode } from '~/utils/format';
import {
    classNames,
    surfaceColorVar,
    textColorVar,
    type IconColor,
    type SurfaceColor,
    type TextColor,
} from '~/utils/styles';

type ExtractSupportColor<T> = T extends `support-${infer Name}-${string}` ? Name : never;
export type BackgroundSupportColor = ExtractSupportColor<SurfaceColor> | 'grey';

type AvatarSizes = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
export type AvatarProps = {
    size?: AvatarSizes;
    color?: BackgroundSupportColor;
    variant?: 'light' | 'base';
} & (
    | {
          // typology 'text';
          text: string;
          icon?: never;
          image?: never;
      }
    | {
          // typology 'icon';
          text?: never;
          icon: IconNames;
          image?: never;
      }
    | {
          // typology 'image';
          text?: never;
          icon?: never;
          image: string;
      }
) &
    HTMLAttributes<HTMLDivElement>;

export default function Avatar({
    size = 'medium',
    color,
    icon,
    text,
    image,
    variant = 'base',
    className = '',
    ...divProps
}: AvatarProps) {
    // color, or random from text, or grey
    const avatarColor = color ?? (text?.[0] ? getRandomColor(text?.[0]) : 'grey');

    const surfaceColorName =
        avatarColor === 'gradient' && variant === 'base'
            ? 'support-gradient-image'
            : `support-${avatarColor}-${variant}`;

    const textColorName = variant === 'light' ? `support-${avatarColor}` : 'support-contrast';

    const surfaceStyle = {
        '--avatar-background': `var(${surfaceColorVar(surfaceColorName as SurfaceColor)})`,
        '--avatar-text-color': `var(${textColorVar(textColorName as TextColor)})`,
    };

    return (
        <div
            {...divProps}
            className={classNames({
                [`avatar-component size-${size}`]: true,
                [className]: Boolean(className),
            })}
            style={surfaceStyle}
            data-test-component='new/Avatar'>
            {/* typology text */}
            {text !== undefined && (
                <span
                    className={`text-size-${size}`}
                    data-test-name='text'>
                    {text}
                </span>
            )}

            {/* typology icon */}
            {icon !== undefined && (
                <Icon
                    icon={icon}
                    size={ICON_SIZES[size]}
                    colorVar={getIconColorVar(avatarColor, variant)}
                />
            )}

            {/* typology image */}
            {image !== undefined && (
                <div className='avatar-image'>
                    <LazyLoadImage
                        visibleByDefault={size === 'xsmall'}
                        src={image}
                        alt='Avatar'
                        effect='opacity'
                    />
                </div>
            )}
        </div>
    );
}

const ICON_SIZES: Record<AvatarSizes, number> = {
    xsmall: 0, // not supported
    small: 12,
    medium: 16,
    large: 24,
    xlarge: 32,
    xxlarge: 48,
};

export function getRandomColor(text: string): BackgroundSupportColor {
    const colors = ['blue', 'cranberry', 'green', 'orange', 'pink', 'purple', 'red', 'yellow']; // no grey, no gradient
    return colors[text.charCodeAt(0) % colors.length] as BackgroundSupportColor;
}

function getIconColorVar(color: BackgroundSupportColor, variant: 'base' | 'light'): IconColor {
    if (variant === 'base') return 'support-contrast';

    switch (color) {
        case 'grey':
        case 'gradient':
            return 'support-grey';
        default:
            return `support-${color}-base`;
    }
}

// Avatar props without typology
export type AvatarUserProps = Omit<AvatarProps, 'icon' | 'text' | 'image'> & {
    user: { first_name: string; last_name: string; picture?: string };
    zoomable?: boolean;
};
export function AvatarUser({
    user,
    zoomable = false,
    variant = 'base',
    ...props
}: AvatarUserProps) {
    return user.picture ? (
        <ZoomableAvatarWrapper
            picture={user.picture}
            zoomable={zoomable}>
            <Avatar
                {...props}
                image={user.picture}
            />
        </ZoomableAvatarWrapper>
    ) : (
        <Avatar
            {...props}
            variant={variant}
            text={`${user.first_name[0] ?? ''}`}
        />
    );
}

function ZoomableAvatarWrapper({
    picture,
    zoomable = false,
    children,
}: {
    picture: string;
    zoomable?: boolean;
    children: ReactElement<AvatarProps>;
}) {
    const zoomImageModal = useModal();
    const zoomedRef = useRef<HTMLDivElement>(null!);

    const handleClick = () => {
        if (zoomable && !zoomImageModal.isOpen) {
            zoomImageModal.open();
        }
    };

    useOnClickOutside(zoomedRef, () => {
        if (zoomImageModal.isOpen) {
            zoomImageModal.close();
        }
    });

    return (
        <>
            {zoomable
                ? cloneElement(children, {
                      className: classNames({ 'cursor-zoom-in': zoomable }),
                      onClick: handleClick,
                  })
                : children}
            {zoomImageModal.isOpen &&
                createPortal(
                    <div
                        ref={zoomedRef}
                        className='RemotelyTheme modal-center-content modal-fixed modal-overlay'
                        onClick={() => zoomImageModal.close()}>
                        <div className='modal-body modal-center-content'>
                            <LazyLoadImage
                                src={picture}
                                className='rounded-full cursor-zoom-out w-120 h-120 object-cover'
                            />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}

// Avatar props without typology
export type AvatarCompanyProps = Omit<AvatarProps, 'icon' | 'text' | 'image'> & {
    // accept Company with optional logo and mandatory name.
    company: Pick<Company, 'name'> & Partial<Pick<Company, 'logo'>>;
};

export function AvatarCompany({ company, ...props }: AvatarCompanyProps) {
    return company.logo ? (
        <Avatar
            {...props}
            image={company.logo}
        />
    ) : (
        <Avatar
            {...props}
            text={company.name[0]}
        />
    );
}

// Avatar props without typology, color or variant
export type AvatarCountryProps = Omit<
    AvatarProps,
    'icon' | 'text' | 'image' | 'color' | 'variant'
> & {
    country: CountryCode;
};
export function AvatarCountry({ country, ...props }: AvatarCountryProps) {
    return (
        <Avatar
            {...props}
            image={`${import.meta.env.BASE_URL ?? '/'}flags/${country.toLowerCase()}.svg`}
        />
    );
}

export type LabeledAvatarProps = Omit<AvatarProps, 'icon' | 'text'> & {
    text: string;
    linkTo?: string;
    target?: string;
};

export function LabeledAvatar({
    text,
    image,
    linkTo = '',
    target,
    className = '',
    ...avatarProps
}: LabeledAvatarProps) {
    return (
        <div
            className={classNames({
                'labeled-avatar labeled-avatar-font-style flex gap-2 items-center overflow-hidden': true,
                [className]: Boolean(className),
            })}>
            {image ? (
                <Avatar
                    {...avatarProps}
                    image={image}
                />
            ) : (
                <Avatar
                    {...avatarProps}
                    text={text[0]}
                />
            )}
            {linkTo ? (
                <Link
                    to={linkTo}
                    target={target}
                    className={classNames({ ellipsis: true })}>
                    {text}
                </Link>
            ) : (
                <Tooltip
                    title={text}
                    overflowOnly>
                    <span className='ellipsis'>{text}</span>
                </Tooltip>
            )}
        </div>
    );
}
